import * as React from "react";

import { substringMatchRange } from "./substringFilter";

export interface SubstringHighlightProps {
  /** Full text to match the query against. */
  text: string;
  /** Search query string. */
  query: string;
  /** When true, use partial matching — highlights the longest prefix of the
   *  query that `text` contains even when the full query is absent. Useful for
   *  nodes whose path only covers a prefix of the query. */
  partial?: boolean;
  /** Optional `[start, end)` range — only characters within this slice of
   *  `text` are rendered, but matching runs against the full `text`. Useful
   *  for rendering just the label segment of a dotted path. */
  slice?: [number, number];
  /** Class applied to each `<mark>` wrapping matched characters. Omit to fall
   *  back to the browser's default highlight styling. */
  markClassName?: string;
}

/**
 * Renders `text` (or a slice of it) with the substring matched by `query`
 * wrapped in a `<mark>`. When `query` is empty or produces no match the plain
 * text is returned. Shared by the metrics filter tree and the Session Metrics
 * tab so both highlight matches identically.
 */
export function SubstringHighlight({
  text,
  query,
  partial,
  slice,
  markClassName,
}: SubstringHighlightProps) {
  const match = React.useMemo(
    () =>
      query.trim() === ""
        ? null
        : substringMatchRange(query, text, { partial }),
    [query, text, partial],
  );

  const start = slice ? slice[0] : 0;
  const end = slice ? slice[1] : text.length;

  // Matching runs against the whole `text` but only the slice is rendered, so
  // clip the match to it: a hit can start before the slice, run past its end,
  // or fall outside it entirely (in which case the clipped range is empty).
  const markStart = Math.max(match?.start ?? end, start);
  const markEnd = Math.min(match?.end ?? start, end);

  if (markStart >= markEnd) {
    return <>{text.slice(start, end)}</>;
  }

  return (
    <>
      {text.slice(start, markStart)}
      <mark className={markClassName}>{text.slice(markStart, markEnd)}</mark>
      {text.slice(markEnd, end)}
    </>
  );
}
